import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { ApiClient } from '../utils/apiClient';
import ConversationTabs from '../components/ConversationTabs';
import FilterModule from '../modules/filter/FilterModule.jsx';
import { ModuleContextProvider } from '../modules/context/ModuleContextProvider.jsx';
import ConversationList from '../components/ConversationList';
import ConversationDetails from '../components/ConversationDetails';
import { useTheme } from '../contexts/ThemeContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';

export default function Conversations() {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const apiClient = useRef(new ApiClient(getToken)).current;
  
  // State management
  const [activeTab, setActiveTab] = useState('completed');
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [filters, setFilters] = useState({
    dateRange: { start: null, end: null },
    parrotName: '',
    phoneNumber: '',
    searchText: ''
  });

  /**
   * Handle filter changes from FilterModule
   */
  const handleFiltersChange = (activeFilters) => {
    // Convert FilterModule activeFilters to legacy filter format
    const newFilters = {
      dateRange: { start: null, end: null },
      parrotName: '',
      phoneNumber: '',
      searchText: ''
    };

    activeFilters.forEach(filter => {
      switch (filter.id) {
        case 'phone_number':
          newFilters.phoneNumber = filter.value;
          break;
        case 'parrot_name':
          newFilters.parrotName = filter.value;
          break;
        case 'date':
          if (filter.operator.type === 'between' && Array.isArray(filter.value)) {
            newFilters.dateRange = { start: filter.value[0], end: filter.value[1] };
          } else if (filter.operator.type === 'greater-than') {
            newFilters.dateRange = { start: filter.value, end: null };
          } else if (filter.operator.type === 'less-than') {
            newFilters.dateRange = { start: null, end: filter.value };
          }
          break;
        default:
          // Use first text filter as general search
          if (filter.type === 'text' && !newFilters.searchText) {
            newFilters.searchText = filter.value;
          }
          break;
      }
    });

    setFilters(newFilters);
  };

  // Load conversations data
  useEffect(() => {
    const loadConversations = async () => {
      if (!isLoaded || !isSignedIn) return;
      
      try {
        setLoading(true);
        setError(null);
        
        const response = await apiClient.get('/api/conversations', {
          params: {
            status: activeTab,
            ...filters
          }
        });
        
        setConversations(response.conversations || []);
      } catch (error) {
        console.error('Failed to load conversations:', error);
        setError('Failed to load conversations. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadConversations();
  }, [isLoaded, isSignedIn, activeTab, filters, apiClient]);

  // Handle conversation status change
  const handleStatusChange = async (conversationId, newStatus) => {
    try {
      await apiClient.put(`/api/conversations/${conversationId}/status`, {
        status: newStatus
      });
      
      // Update local state optimistically
      setConversations(prev => 
        prev.map(conv => 
          conv.id === conversationId 
            ? { ...conv, status: newStatus }
            : conv
        )
      );
      
      // Close details modal if conversation was moved to different tab
      if (selectedConversation?.id === conversationId && newStatus !== activeTab) {
        setSelectedConversation(null);
      }
    } catch (error) {
      console.error('Failed to update conversation status:', error);
      // TODO: Show error toast
    }
  };

  // Handle conversation deletion (soft delete)
  const handleDelete = async (conversationId) => {
    try {
      await apiClient.delete(`/api/conversations/${conversationId}`);

      // Remove from local state
      setConversations(prev =>
        prev.filter(conv => conv.id !== conversationId)
      );

      // Close details modal if deleted conversation was selected
      if (selectedConversation?.id === conversationId) {
        setSelectedConversation(null);
      }
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      // TODO: Show error toast
    }
  };

  // Handle conversation selection and mark as viewed
  const handleSelectConversation = async (conversation) => {
    setSelectedConversation(conversation);

    // Mark conversation as viewed if it hasn't been viewed yet
    if (!conversation.last_viewed_at) {
      try {
        await apiClient.put(`/api/conversations/${conversation.id}/view`);
        // Update local state to reflect that it's been viewed
        setConversations(prev =>
          prev.map(conv =>
            conv.id === conversation.id
              ? { ...conv, last_viewed_at: new Date().toISOString() }
              : conv
          )
        );
      } catch (error) {
        console.error('Failed to mark conversation as viewed:', error);
        // Don't show error to user as this is not critical functionality
      }
    }
  };

  // Calculate conversation counts for tabs
  // Map frontend tab names to database statuses
  const statusMapping = {
    'completed': ['completed'],
    'active': ['connected', 'in-progress', 'connecting'],
    'failed': ['failed', 'initiated']
  };

  const getConversationCounts = () => {
    const counts = { completed: 0, active: 0, failed: 0 };
    conversations.forEach(conv => {
      // Map database status to frontend tab
      Object.entries(statusMapping).forEach(([tabName, dbStatuses]) => {
        if (dbStatuses.includes(conv.status)) {
          counts[tabName]++;
        }
      });
    });
    return counts;
  };

  const conversationCounts = getConversationCounts();

  // Tab configuration
  const tabs = [
    { id: 'completed', label: 'Completed', count: conversationCounts.completed },
    { id: 'active', label: 'Active', count: conversationCounts.active },
    { id: 'failed', label: 'Failed', count: conversationCounts.failed }
  ];

  // Filter conversations by active tab using status mapping
  const filteredConversations = conversations.filter(conv =>
    statusMapping[activeTab]?.includes(conv.status)
  );

  return (
    <div className="space-y-6">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Conversations</h1>
        <p className="text-gray-600 dark:text-gray-400 dark:text-gray-300 dark:text-gray-600">
          Manage and review all your AI agent conversations
        </p>
      </div>

      {/* Tab Navigation */}
      <ConversationTabs 
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* Filters */}
      <ModuleContextProvider
        user={{ id: 'user', permissions: ['user'] }}
        permissions={[{ id: 'user', name: 'User Access' }]}
        apiClient={apiClient}
        theme={{ mode: theme }}
        toast={{ success: showToast, error: showToast, warning: showToast, info: showToast }}
      >
        <FilterModule
          config={{
            context: 'conversations',
            filters: [], // Will use context-specific defaults
            layout: 'horizontal',
            persistFilters: true,
            storageKey: 'conversations-filters',
            showClearAll: true,
            realTimeFiltering: true,
            debounceMs: 300,
            onFilterChange: handleFiltersChange,
            onFilterReset: () => setFilters({
              dateRange: { start: null, end: null },
              parrotName: '',
              phoneNumber: '',
              searchText: ''
            })
          }}
          className="mb-6"
          testId="conversations-filter"
        />
      </ModuleContextProvider>

      {/* Conversation List */}
      <ConversationList
        conversations={filteredConversations}
        loading={loading}
        error={error}
        onSelect={handleSelectConversation}
        onStatusChange={handleStatusChange}
        onDelete={handleDelete}
      />

      {/* Conversation Details Modal */}
      {selectedConversation && (
        <ConversationDetails
          conversation={selectedConversation}
          onClose={() => setSelectedConversation(null)}
          onStatusChange={handleStatusChange}
          onDelete={handleDelete}
        />
      )}
    </div>
  );
}
